import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../pics/FOOT ICON2.svg';

export const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { path: '/', label: 'Home' },
        { path: '/services', label: 'Treatments' },
        { path: '/about', label: 'About' },
        { path: '/blog', label: 'Blog' }
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <motion.header 
            className={`fixed w-full z-50 transition-all duration-300 ${
                isScrolled 
                    ? 'bg-white/95 backdrop-blur-md shadow-lg' 
                    : 'bg-white/80 backdrop-blur-sm shadow-sm'
            }`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                    {/* Logo */}
                    <motion.div 
                        className="flex items-center"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                    >
                        <Link className="flex items-center hover:opacity-80 transition-opacity" to="/">
                            <motion.img 
                                src={Logo} 
                                alt="Logo" 
                                className="w-10 lg:w-12"
                                whileHover={{ rotate: 5 }}
                                transition={{ duration: 0.3 }}
                            />
                            <div className="font-sans -ml-4">
                                <h1 className='text-lg leading-none lg:text-md font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent'>
                                    Drosh
                                </h1>
                                <h1 className='text-sm font-thin leading-none lg:text-md text-gray-600'>
                                    The
                                </h1>
                                <h1 className='text-lg leading-none text-primary lg:text-md font-bold'>
                                    POD
                                </h1>
                            </div>
                        </Link>
                    </motion.div>

                    {/* Mobile Menu Button */}
                    <motion.button
                        className="md:hidden rounded-lg p-2 hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 transition-all duration-300"
                        onClick={toggleMenu}
                        aria-label="Toggle mobile menu"
                        whileTap={{ scale: 0.95 }}
                    >
                        <motion.svg
                            className="w-6 h-6 text-gray-600"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            animate={{ rotate: isMenuOpen ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {isMenuOpen ? (
                                <path d="M18 6L6 18M6 6l12 12" />
                            ) : (
                                <path d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </motion.svg>
                    </motion.button>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navItems.map((item, index) => (
                            <motion.div
                                key={item.path}
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Link
                                    className={`relative text-sm font-medium transition-all duration-300 group ${
                                        isActive(item.path)
                                            ? 'text-primary'
                                            : 'text-gray-600 hover:text-primary'
                                    }`}
                                    to={item.path}
                                >
                                    {item.label}
                                    <motion.div
                                        className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full ${
                                            isActive(item.path) ? 'w-full' : 'w-0 group-hover:w-full'
                                        }`}
                                        transition={{ duration: 0.3 }}
                                    />
                                </Link>
                            </motion.div>
                        ))}
                        

                        {/* CTA Button */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            <Link
                                to="/ConsultationForm"
                                className="px-6 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-full text-sm font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                            >
                                Book Now
                            </Link>
                        </motion.div>
                    </nav>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div 
                        className="absolute top-16 left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg md:hidden"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <nav className="flex flex-col p-4 space-y-2">
                            {navItems.map((item, index) => (
                                <motion.div
                                    key={item.path}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                >
                                    <Link
                                        className={`block py-3 px-4 rounded-lg transition-all duration-300 ${
                                            isActive(item.path)
                                                ? 'bg-gradient-to-r from-primary/10 to-secondary/10 text-primary font-semibold'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                                        }`}
                                        to={item.path}
                                        onClick={toggleMenu}
                                    >
                                        {item.label}
                                    </Link>
                                </motion.div>
                            ))}
                            
                            {/* Mobile CTA */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: 0.4 }}
                                className="pt-2"
                            >
                                <Link
                                    to="/ConsultationForm"
                                    className="block py-3 px-4 bg-gradient-to-r from-primary to-secondary text-white rounded-lg text-center font-semibold hover:shadow-lg transition-all duration-300"
                                    onClick={toggleMenu}
                                >
                                    Book Consultation
                                </Link>
                            </motion.div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;